import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { useAuth } from "../context/AuthContext";

const initialForm = {
  user_id: "",
  task_given: "",
  date_assigned: "",
  date_due: "",
  report_required: false
};

function StaffTaskFormPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(uuid);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const role = user?.role || "";

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchTask();
    }
  }, [uuid]);

  const submitLabel = useMemo(() => (isEditing ? "Update task" : "Create task"), [isEditing]);
  const currentUserRecord = useMemo(
    () => users.find((candidate) => candidate.uuid === user?.uuid) || null,
    [user?.uuid, users]
  );
  const selectedAssignedUserRecord = useMemo(
    () => users.find((candidate) => String(candidate.id) === String(form.user_id || "")) || null,
    [form.user_id, users]
  );
  const eligibleUsers = useMemo(() => {
    if (role === "staff") {
      return currentUserRecord ? [currentUserRecord] : [];
    }

    if (role === "supervisor") {
      return users.filter((candidate) => String(candidate.supervisor_id || "") === String(currentUserRecord?.id || ""));
    }

    return users;
  }, [currentUserRecord, role, users]);
  const assigneeUsers = useMemo(() => {
    if (!selectedAssignedUserRecord) {
      return eligibleUsers;
    }

    if (eligibleUsers.some((candidate) => candidate.id === selectedAssignedUserRecord.id)) {
      return eligibleUsers;
    }

    return [...eligibleUsers, selectedAssignedUserRecord];
  }, [eligibleUsers, selectedAssignedUserRecord]);
  const userOptions = useMemo(
    () =>
      assigneeUsers.map((staffUser) => ({
        value: String(staffUser.id),
        label: [staffUser.firstname, staffUser.lastname].filter(Boolean).join(" ") || staffUser.username
      })),
    [assigneeUsers]
  );
  const selectedUserOption = useMemo(
    () => userOptions.find((option) => option.value === String(form.user_id)) || null,
    [form.user_id, userOptions]
  );

  useEffect(() => {
    if (role === "staff" && currentUserRecord && !isEditing) {
      setForm((current) => ({
        ...current,
        user_id: String(currentUserRecord.id)
      }));
    }
  }, [currentUserRecord, isEditing, role]);

  async function fetchUsers() {
    try {
      const response = await api.get("/users");
      setUsers(response.data || []);
    } catch (err) {
      setError("Failed to load users.");
    }
  }

  async function fetchTask() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/staff-tasks/${uuid}`);
      const task = response.data;
      setForm({
        user_id: task.user_id || "",
        task_given: task.task_given || "",
        date_assigned: task.date_assigned || "",
        date_due: task.date_due || "",
        report_required: Boolean(task.report_required)
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load staff task.");
    } finally {
      setLoading(false);
    }
  }

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);

    const payload = {
      user_id: Number(form.user_id),
      task_given: form.task_given,
      date_assigned: form.date_assigned,
      date_due: form.date_due || null,
      report_required: form.report_required
    };

    try {
      if (isEditing) {
        await api.put(`/staff-tasks/${uuid}`, payload);
        toast.success("Staff task updated");
      } else {
        await api.post("/staff-tasks", payload);
        toast.success("Staff task created");
      }

      history.push("/staff-tasks");
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${isEditing ? "update" : "create"} staff task.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row className="justify-content-center">
      <Col xs={12} xl={10}>
        <PageHeader
          title={isEditing ? "Edit Staff Task" : "Add Staff Task"}
          action={
            <Button variant="outline-secondary" onClick={() => history.push("/staff-tasks")}>
              Back to staff tasks
            </Button>
          }
        />

        {loading ? <LoadingState label="Loading staff task..." /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}

        {!loading ? (
          <div className="content-card form-card">
            <form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={4}>
                  <label className="form-label">Assigned To</label>
                  {role === "staff" ? (
                    <Form.Control
                      value={
                        currentUserRecord
                          ? [currentUserRecord.firstname, currentUserRecord.lastname].filter(Boolean).join(" ") || currentUserRecord.username
                          : ""
                      }
                      readOnly
                      plaintext={false}
                    />
                  ) : (
                    <Select
                      inputId="staff-task-user-id"
                      classNamePrefix="react-select"
                      options={userOptions}
                      value={selectedUserOption}
                      onChange={(option) =>
                        setForm((current) => ({
                          ...current,
                          user_id: option?.value || ""
                        }))
                      }
                      placeholder={role === "supervisor" ? "Select staff member" : "Select user"}
                      isClearable
                      isSearchable
                    />
                  )}
                </Col>
                <Col md={4}>
                  <label className="form-label">Date assigned</label>
                  <Form.Control type="date" name="date_assigned" value={form.date_assigned} onChange={handleChange} required />
                </Col>
                <Col md={4}>
                  <label className="form-label">Date due</label>
                  <Form.Control type="date" name="date_due" value={form.date_due} onChange={handleChange} />
                </Col>
                <Col xs={12}>
                  <label className="form-label">Task given</label>
                  <Form.Control as="textarea" rows={4} name="task_given" value={form.task_given} onChange={handleChange} required />
                </Col>
                <Col md={4}>
                  <div className="form-check mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="report_required"
                      checked={form.report_required}
                      onChange={handleChange}
                      id="staffTaskReportRequired"
                    />
                    <label className="form-check-label" htmlFor="staffTaskReportRequired">
                      Report required
                    </label>
                  </div>
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : submitLabel}
                </Button>
                <Button variant="outline-secondary" onClick={() => history.push("/staff-tasks")}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export default StaffTaskFormPage;
